'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const strategies = [
    {
        num: '01',
        title: 'Landing Page Optimization',
        body: 'We enhanced the quality, relevance, and structure of key landing pages to improve user engagement, keyword targeting, and search engine rankings.',
    },
    {
        num: '02',
        title: 'Authority Building Through Guest Posts & Digital PR',
        body: 'A strong backlink acquisition strategy was implemented through high-quality guest posting and digital PR campaigns — boosting domain authority from 10 to 22 within six months.',
    },
    {
        num: '03',
        title: 'Expanding Brand Visibility Across Social Platforms',
        body: 'We strengthened the brand\'s presence on Quora, Pinterest, and LinkedIn to increase visibility, drive referral traffic, and support overall search performance through enhanced brand signals.',
    },
    {
        num: '04',
        title: 'Website Speed & Performance Optimization',
        body: 'A complete Shopify theme revamp improved website speed and performance — resulting in better user experience, lower bounce rates, and stronger SEO performance.',
    },
    {
        num: '05',
        title: 'SEO-Focused Content Strategy',
        body: 'We developed keyword-rich product descriptions and consistently published 5–10 SEO-focused blog articles every month. Several secured Top 3 rankings, driving substantial organic traffic growth.',
    },
    {
        num: '06',
        title: 'Toxic Backlink Cleanup & Spam Score Reduction',
        body: 'Through a comprehensive backlink audit and disavow process, we reduced the spam score from high to just 1%, significantly improving overall SEO health.',
    },
    {
        num: '07',
        title: 'Conversion Rate Optimization',
        body: 'We introduced a one-click checkout process to reduce friction during the purchase journey — maximizing conversions from both organic and paid traffic sources.',
    },
];

export default function FashionBrandSEOPage() {
    const observerRefs = useRef<(HTMLElement | null)[]>([]);

    useEffect(() => {
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    (e.target as HTMLElement).classList.add('cs-visible');
                    obs.unobserve(e.target);
                }
            });
        }, { threshold: 0.08 });
        observerRefs.current.forEach(el => { if (el) obs.observe(el); });
        return () => obs.disconnect();
    }, []);

    const ref = (i: number) => (el: HTMLElement | null) => { observerRefs.current[i] = el; };

    return (
        <>
            <style>{`
                .cs-fade { opacity: 0; transform: translateY(32px); transition: opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1); }
                .cs-fade.cs-visible { opacity: 1; transform: none; }
                .cs-scale { opacity: 0; transform: scale(0.95); transition: opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1); }
                .cs-scale.cs-visible { opacity: 1; transform: none; }
                .gsc-img { border-radius: 16px; box-shadow: 0 24px 80px rgba(0,0,0,0.45); transition: transform 0.5s ease, box-shadow 0.5s ease; }
                .gsc-img:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 40px 100px rgba(0,0,0,0.6); }
                .cs-cta-btn:hover { transform: scale(1.05); box-shadow: 0 8px 40px rgba(206,255,26,0.35); }
                .cs-back-link:hover { color: rgba(251,253,253,0.8) !important; }
                @media (max-width: 900px) {
                    .cs-hero-grid, .cs-before-grid, .cs-after-grid, .cs-outcome-grid { flex-direction: column !important; }
                    .cs-after-grid { flex-direction: column-reverse !important; }
                    .cs-inner { padding: 40px 24px !important; }
                    .cs-stat-row { flex-wrap: wrap !important; }
                    .cs-container { padding-left: 20px !important; padding-right: 20px !important; }
                    .cs-text-col { flex: 1 1 auto !important; max-width: 100% !important; }
                    .cs-stats-col { min-width: 0 !important; width: 100% !important; }
                }
            `}</style>

            <main style={{ background: '#080400', minHeight: '100vh' }}>
                <div style={{ height: 100 }} />

                {/* ══════════════════════════════════
                    HERO
                ══════════════════════════════════ */}
                <section style={{ padding: '60px 0 0', position: 'relative', overflow: 'hidden' }}>
                    {/* subtle grid bg */}
                    <div style={{
                        position: 'absolute', inset: 0, opacity: 0.025,
                        backgroundImage: 'linear-gradient(rgba(206,255,26,1) 1px, transparent 1px), linear-gradient(90deg, rgba(206,255,26,1) 1px, transparent 1px)',
                        backgroundSize: '48px 48px',
                    }} />

                    <div className="cs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px', position: 'relative', zIndex: 1 }}>

                        {/* Breadcrumb */}
                        <div ref={ref(0)} className="cs-fade" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 52 }}>
                            <Link href="/our-work/case-studies" style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'rgba(251,253,253,0.3)', textDecoration: 'none', letterSpacing: '0.06em', transition: 'color 0.2s' }}>Case Studies</Link>
                            <span style={{ color: 'rgba(251,253,253,0.15)', fontSize: 13 }}>›</span>
                            <span style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'rgba(251,253,253,0.5)', letterSpacing: '0.06em' }}>Fashion Brand SEO</span>
                        </div>

                        {/* Hero grid */}
                        <div className="cs-hero-grid" style={{ display: 'flex', gap: 64, alignItems: 'center' }}>
                            {/* Left: text */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div ref={ref(1)} className="cs-fade" style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    background: 'rgba(206,255,26,0.07)', border: '1px solid rgba(206,255,26,0.2)',
                                    borderRadius: 50, padding: '6px 18px', marginBottom: 28,
                                }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ceff1a', display: 'inline-block' }} />
                                    <span style={{ fontFamily: 'var(--font-manrope)', fontWeight: 700, fontSize: 11, color: '#ceff1a', letterSpacing: '0.18em', textTransform: 'uppercase' }}>Fashion & Apparel · SEO</span>
                                </div>

                                <h1 ref={ref(2)} className="cs-fade" style={{
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontWeight: 700, fontSize: 'clamp(36px, 5vw, 68px)',
                                    color: '#fbfdfd', letterSpacing: '0.03em', lineHeight: 1.08, margin: '0 0 28px',
                                }}>
                                    Fashion Brand SEO:<br />
                                    <span style={{ color: '#ceff1a' }}>1,000% Growth</span><br />in 6 Months
                                </h1>

                                <p ref={ref(3)} className="cs-fade" style={{
                                    fontFamily: 'var(--font-manrope)', fontSize: 'clamp(15px, 1.3vw, 18px)',
                                    color: 'rgba(251,253,253,0.45)', lineHeight: 1.85, letterSpacing: '0.02em',
                                    maxWidth: 520, margin: '0 0 48px',
                                }}>
                                    From 671 monthly clicks to 7,750+ — how a structured SEO strategy delivered over 1,000% organic traffic growth for a fashion e-commerce brand in just six months.
                                </p>

                                {/* Stat chips */}
                                <div ref={ref(4)} className="cs-fade cs-stat-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    {[
                                        { val: '1,000%+', lbl: 'Traffic Growth' },
                                        { val: '418K+', lbl: 'Impressions/mo' },
                                        { val: 'DA 22', lbl: 'Domain Authority' },
                                        { val: '1%', lbl: 'Spam Score' },
                                    ].map(s => (
                                        <div key={s.val} style={{
                                            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                                            borderRadius: 14, padding: '14px 22px', textAlign: 'center',
                                        }}>
                                            <div style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(18px, 2.2vw, 28px)', color: '#ceff1a', letterSpacing: '-0.01em' }}>{s.val}</div>
                                            <div style={{ fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 11, color: 'rgba(251,253,253,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>{s.lbl}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right: cover image */}
                            <div ref={ref(5)} className="cs-scale" style={{ flexShrink: 0, width: 'clamp(260px, 36vw, 520px)' }}>
                                <Image src="/images/our-work/fashion-brand-seo-cover.webp" alt="Fashion Brand SEO"
                                    width={520} height={380} style={{ width: '100%', height: 'auto', borderRadius: 28, display: 'block', boxShadow: '0 40px 120px rgba(0,0,0,0.6)' }} />
                            </div>
                        </div>
                    </div>

                    <div style={{ height: 100, background: 'linear-gradient(transparent, #080400)', marginTop: 64 }} />
                </section>

                {/* ══════════════════════════════════
                    BEFORE — GSC Screenshot 1
                ══════════════════════════════════ */}
                <section className="cs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 100px' }}>
                    <div ref={ref(6)} className="cs-fade cs-inner" style={{
                        background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 32, padding: '56px', position: 'relative', overflow: 'hidden',
                    }}>
                        {/* Top shimmer line */}
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)' }} />

                        <div className="cs-before-grid" style={{ display: 'flex', gap: 64, alignItems: 'center' }}>
                            <div className="cs-text-col" style={{ flex: '0 0 auto', maxWidth: 340 }}>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 11, color: 'rgba(251,253,253,0.25)', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 14px' }}>The Challenge</p>
                                <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(22px, 2.8vw, 38px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 18px', lineHeight: 1.2 }}>
                                    Starting Point<br />— July 2023
                                </h2>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 15, color: 'rgba(251,253,253,0.4)', lineHeight: 1.85, margin: '0 0 32px', letterSpacing: '0.02em' }}>
                                    The client had limited organic visibility despite a strong product range. Here's what Google Search Console showed when we began.
                                </p>
                                <div style={{ display: 'flex', gap: 32 }}>
                                    {[{ val: '671', lbl: 'Monthly Clicks' }, { val: '67.8K', lbl: 'Impressions' }].map(m => (
                                        <div key={m.val}>
                                            <div style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 36, color: 'rgba(251,253,253,0.2)', lineHeight: 1 }}>{m.val}</div>
                                            <div style={{ fontFamily: 'var(--font-manrope)', fontSize: 11, color: 'rgba(251,253,253,0.18)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 5 }}>{m.lbl}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div ref={ref(7)} className="cs-scale" style={{ flex: 1, minWidth: 0 }}>
                                <Image src="/images/case-studies/fashion-seo/gsc-screenshot-1-1.webp"
                                    alt="Google Search Console — Starting Metrics Jul 2023"
                                    width={800} height={450} className="gsc-img" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 12, color: 'rgba(251,253,253,0.2)', letterSpacing: '0.08em', textAlign: 'center', marginTop: 14 }}>
                                    Google Search Console · Jul 1–31, 2023
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ══════════════════════════════════
                    AFTER — GSC Screenshot 2
                ══════════════════════════════════ */}
                <section className="cs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 100px' }}>
                    <div ref={ref(8)} className="cs-fade cs-inner" style={{
                        background: 'rgba(206,255,26,0.03)', border: '1.5px solid rgba(206,255,26,0.13)',
                        borderRadius: 32, padding: '56px', position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(206,255,26,0.5), transparent)' }} />

                        <div className="cs-after-grid" style={{ display: 'flex', gap: 64, alignItems: 'center', flexDirection: 'row-reverse' }}>
                            <div className="cs-text-col" style={{ flex: '0 0 auto', maxWidth: 340 }}>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 11, color: '#ceff1a', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 14px' }}>Month 6 Results</p>
                                <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(22px, 2.8vw, 38px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 18px', lineHeight: 1.2 }}>
                                    After — December 2023
                                </h2>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 15, color: 'rgba(251,253,253,0.4)', lineHeight: 1.85, margin: '0 0 32px', letterSpacing: '0.02em' }}>
                                    Six months later, the brand's GSC data told a completely different story — consistent high traffic and strong click-through rates across all key pages.
                                </p>
                                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                                    {[{ val: '7.75K', lbl: 'Clicks' }, { val: '418K', lbl: 'Impressions' }, { val: '1.9%', lbl: 'CTR' }].map(m => (
                                        <div key={m.val}>
                                            <div style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 36, color: '#ceff1a', lineHeight: 1 }}>{m.val}</div>
                                            <div style={{ fontFamily: 'var(--font-manrope)', fontSize: 11, color: 'rgba(251,253,253,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 5 }}>{m.lbl}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div ref={ref(9)} className="cs-scale" style={{ flex: 1, minWidth: 0 }}>
                                <Image src="/images/case-studies/fashion-seo/gsc-screenshot-2-1.webp"
                                    alt="Google Search Console — Results Dec 2023"
                                    width={800} height={450} className="gsc-img" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 12, color: 'rgba(251,253,253,0.2)', letterSpacing: '0.08em', textAlign: 'center', marginTop: 14 }}>
                                    Google Search Console · Dec 1–31, 2023
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ══════════════════════════════════
                    STRATEGIES
                ══════════════════════════════════ */}
                <section className="cs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 48px 120px' }}>
                    <div ref={ref(10)} className="cs-fade" style={{ marginBottom: 72 }}>
                        <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 12, color: '#ceff1a', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 18px' }}>How We Did It</p>
                        <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(30px, 4.5vw, 58px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: 0, lineHeight: 1.1 }}>
                            7 Strategies.<br />One Breakthrough.
                        </h2>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        {strategies.map((s, i) => (
                            <div
                                key={s.num}
                                ref={ref(11 + i)}
                                className="cs-fade"
                                style={{
                                    display: 'flex', gap: 'clamp(20px, 4vw, 80px)', alignItems: 'flex-start',
                                    padding: '44px 0',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    transitionDelay: `${i * 0.06}s`,
                                }}
                            >
                                <div style={{
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontWeight: 700, fontSize: 'clamp(44px, 5.5vw, 80px)',
                                    color: 'rgba(206,255,26,0.08)', lineHeight: 1, flexShrink: 0,
                                    minWidth: 'clamp(56px, 7.5vw, 104px)', letterSpacing: '-0.03em',
                                }}>{s.num}</div>
                                <div style={{ flex: 1, paddingTop: 8 }}>
                                    <h3 style={{
                                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                        fontWeight: 600, fontSize: 'clamp(17px, 1.8vw, 24px)',
                                        color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 10px', lineHeight: 1.3,
                                    }}>{s.title}</h3>
                                    <p style={{
                                        fontFamily: 'var(--font-manrope)', fontSize: 'clamp(14px, 1.1vw, 16px)',
                                        color: 'rgba(251,253,253,0.4)', lineHeight: 1.9, letterSpacing: '0.02em', margin: 0,
                                    }}>{s.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ══════════════════════════════════
                    FULL 6-MONTH GROWTH SNAPSHOT — GSC Screenshot 3
                ══════════════════════════════════ */}
                <section className="cs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 120px' }}>
                    <div ref={ref(18)} className="cs-fade" style={{ marginBottom: 48 }}>
                        <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 12, color: '#ceff1a', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 16px' }}>Growth Snapshot</p>
                        <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(26px, 3.5vw, 50px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 12px', lineHeight: 1.15 }}>
                            Jul – Dec 2023 · Full 6-Month View
                        </h2>
                        <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 15, color: 'rgba(251,253,253,0.35)', letterSpacing: '0.02em', margin: 0 }}>
                            Total clicks: 18.9K · Total impressions: 1.05M · Avg CTR: 1.8% · Avg position: 28.8
                        </p>
                    </div>

                    <div ref={ref(19)} className="cs-scale">
                        <Image src="/images/case-studies/fashion-seo/gsc-screenshot-3-1.webp"
                            alt="Google Search Console — Full 6-month growth Jul–Dec 2023"
                            width={1200} height={600} className="gsc-img" style={{ width: '100%', height: 'auto', display: 'block' }} />
                        <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 12, color: 'rgba(251,253,253,0.2)', letterSpacing: '0.08em', textAlign: 'center', marginTop: 18 }}>
                            Google Search Console · Jul 1 – Dec 31, 2023 · Performance on Search Results
                        </p>
                    </div>
                </section>

                {/* ══════════════════════════════════
                    OUTCOME SUMMARY
                ══════════════════════════════════ */}
                <section className="cs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 120px' }}>
                    <div ref={ref(20)} className="cs-fade cs-inner" style={{
                        background: 'rgba(206,255,26,0.03)', border: '1px solid rgba(206,255,26,0.1)',
                        borderRadius: 32, padding: '64px',
                    }}>
                        <div className="cs-outcome-grid" style={{ display: 'flex', gap: 72, alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 11, color: '#ceff1a', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 16px' }}>The Outcome</p>
                                <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(24px, 3vw, 42px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 20px', lineHeight: 1.2 }}>
                                    Sustainable Growth.<br />Long-Term Results.
                                </h2>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 16, color: 'rgba(251,253,253,0.4)', lineHeight: 1.9, margin: 0, letterSpacing: '0.02em' }}>
                                    Within six months, the brand experienced a significant increase in organic visibility, traffic, and authority. The combination of technical SEO improvements, content optimization, authority building, and conversion-focused enhancements created sustainable long-term growth and stronger search engine performance.
                                </p>
                            </div>

                            <div className="cs-stats-col" style={{ display: 'flex', flexDirection: 'column', gap: 22, flexShrink: 0, minWidth: 280 }}>
                                {[
                                    { label: 'Monthly Clicks', before: '671', after: '7,750+' },
                                    { label: 'Monthly Impressions', before: '67,800', after: '418,000+' },
                                    { label: 'Domain Authority', before: 'DA 10', after: 'DA 22' },
                                    { label: 'Spam Score', before: 'High', after: '1%' },
                                ].map(m => (
                                    <div key={m.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>
                                        <div style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'rgba(251,253,253,0.3)', letterSpacing: '0.05em', minWidth: 120 }}>{m.label}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 600, fontSize: 15, color: 'rgba(251,253,253,0.18)' }}>{m.before}</span>
                                            <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
                                                <path d="M1 5h14M10 1l4 4-4 4" stroke="rgba(206,255,26,0.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 18, color: '#ceff1a' }}>{m.after}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ══════════════════════════════════
                    CTA
                ══════════════════════════════════ */}
                <section style={{ textAlign: 'center', padding: '0 24px 140px' }}>
                    <div ref={ref(21)} className="cs-fade" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
                        <h2 style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            fontWeight: 600, fontSize: 'clamp(22px, 3.5vw, 46px)',
                            color: '#fbfdfd', letterSpacing: '0.03em', margin: 0,
                        }}>Ready to grow your organic traffic?</h2>
                        <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 17, color: 'rgba(251,253,253,0.35)', margin: 0, maxWidth: 460, lineHeight: 1.75, letterSpacing: '0.02em' }}>
                            Let's build an SEO strategy that delivers real, measurable results for your brand.
                        </p>
                        <Link href="/contact" className="cs-cta-btn" style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            background: '#ceff1a', color: '#080400',
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            fontWeight: 600, fontSize: 18, letterSpacing: '0.06em',
                            padding: '18px 52px', borderRadius: 50, textDecoration: 'none',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        }}>
                            Book a Free SEO Audit →
                        </Link>
                        <Link href="/our-work/case-studies" className="cs-back-link" style={{
                            fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 14,
                            color: 'rgba(251,253,253,0.25)', textDecoration: 'none', letterSpacing: '0.05em',
                            transition: 'color 0.2s',
                        }}>
                            ← Back to Case Studies
                        </Link>
                    </div>
                </section>
            </main>
        </>
    );
}
